import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtMoney, fmtQty, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Settings2, Star, StarOff } from "lucide-react";

const STATUS_META = {
  vigente: { label: "Vigente", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  lento: { label: "Lento", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  rezagado: { label: "Rezagado", cls: "bg-red-100 text-red-800 border-red-300" },
  estrategico: { label: "Estratégico", cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  sin_movimiento: { label: "Sin movimiento", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function StockRotation() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [warehouse, setWarehouse] = useState("all");
  const [warehouses, setWarehouses] = useState([]);
  const [setOpen, setSetOpen] = useState(false);
  const [settings, setSettings] = useState({ vigente_max_days: 90, lento_max_days: 180, strategic_items: [] });

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (warehouse !== "all") params.warehouse = warehouse;
    api.get("/stock-rotation", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status, warehouse]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/stock-rotation/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const saveSettings = async () => {
    try {
      const clean = {
        vigente_max_days: Number(settings.vigente_max_days),
        lento_max_days: Number(settings.lento_max_days),
        strategic_items: (typeof settings.strategic_items === "string"
          ? settings.strategic_items.split(/[\s,;]+/).filter(Boolean)
          : settings.strategic_items),
      };
      const { data: saved } = await api.put("/stock-rotation/settings", clean);
      setSettings(saved);
      toast.success("Umbrales guardados");
      setSetOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleStrategic = async (itemNumber, becomeStrategic) => {
    try {
      const list = new Set(settings.strategic_items || []);
      if (becomeStrategic) list.add(itemNumber); else list.delete(itemNumber);
      const clean = {
        vigente_max_days: Number(settings.vigente_max_days),
        lento_max_days: Number(settings.lento_max_days),
        strategic_items: Array.from(list),
      };
      const { data: saved } = await api.put("/stock-rotation/settings", clean);
      setSettings(saved);
      toast.success(becomeStrategic ? "Marcado como estratégico" : "Ya no es estratégico");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const strategicList = Array.isArray(settings.strategic_items)
    ? settings.strategic_items.join(", ")
    : (settings.strategic_items || "");

  return (
    <div data-testid="rotation-page">
      <PageHeader title="Rotación de Existencias" subtitle="Etiquetas Vigente / Lento / Rezagado / Estratégico según fecha de última salida.">
        {can("admin") && (
          <Dialog open={setOpen} onOpenChange={setSetOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="rot-settings-button">
                <Settings2 size={13} className="mr-1" /> Umbrales
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">Umbrales de rotación</DialogTitle>
                <DialogDescription className="text-xs">Días desde la última salida (Cardex negativo).</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">Vigente hasta (días)</Label>
                  <Input type="number" min={1} value={settings.vigente_max_days}
                    onChange={(e) => setSettings({ ...settings, vigente_max_days: e.target.value })}
                    className="rounded-none h-8 text-xs tabular-nums font-black" data-testid="rot-vigente-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">Lento hasta (días)</Label>
                  <Input type="number" min={2} value={settings.lento_max_days}
                    onChange={(e) => setSettings({ ...settings, lento_max_days: e.target.value })}
                    className="rounded-none h-8 text-xs tabular-nums font-black" data-testid="rot-lento-input" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">Artículos estratégicos (item_number, separados por coma)</Label>
                  <Input value={strategicList}
                    onChange={(e) => setSettings({ ...settings, strategic_items: e.target.value })}
                    placeholder="ITM-100, ITM-200, ..."
                    className="rounded-none h-8 text-xs" data-testid="rot-strategic-input" />
                  <p className="text-[9px] text-muted-foreground">
                    &gt; Vigente (≤ {settings.vigente_max_days} d) &nbsp;·&nbsp; Lento ({Number(settings.vigente_max_days) + 1}–{settings.lento_max_days} d) &nbsp;·&nbsp; Rezagado (&gt; {settings.lento_max_days} d)
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveSettings} className="rounded-none font-bold text-xs" data-testid="rot-save-settings">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {/* Summary strip */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="rot-summary">
          {Object.keys(STATUS_META).map((k) => (
            <button key={k} onClick={() => setStatus(status === k ? "all" : k)}
              className={`text-left border p-3 hover:opacity-80 ${status === k ? "ring-2 ring-primary" : ""}`}
              data-testid={`rot-summary-${k}`}>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{STATUS_META[k].label}</div>
              <div className="text-2xl font-black tabular-nums">{data.counts[k] || 0}</div>
              <div className="text-[10px] tabular-nums text-muted-foreground">{fmtMoney(data.total_value_by_status[k] || 0)}</div>
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Estado</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="rot-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.keys(STATUS_META).map((k) => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Almacén</Label>
          <Select value={warehouse} onValueChange={setWarehouse}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="rot-filter-warehouse"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!data ? <div className="text-xs text-muted-foreground">Cargando…</div> : data.items.length === 0 ? <Empty text="Sin coincidencias" /> : (
        <div className="tbl-wrap" data-testid="rot-table">
          <table className="tbl">
            <thead><tr>
              <th></th>
              <th>Artículo</th>
              <th>Descripción</th>
              <th>Almacén</th>
              <th className="num">Existencia</th>
              <th>UOM</th>
              <th className="num">Costo prom.</th>
              <th className="num">Valor</th>
              <th className="num">Últ. salida</th>
              <th className="num">Días</th>
              <th>Estado</th>
            </tr></thead>
            <tbody>
              {data.items.map((r, idx) => {
                const meta = STATUS_META[r.rotation_status] || STATUS_META.sin_movimiento;
                return (
                  <tr key={`${r.item_number}-${r.warehouse}-${r.location}-${idx}`} data-testid={`rot-row-${r.item_number}`}>
                    <td>
                      {can("admin") && (
                        <button onClick={() => toggleStrategic(r.item_number, !r.is_strategic)}
                          className={`hover:opacity-70 ${r.is_strategic ? "text-indigo-600" : "text-muted-foreground"}`}
                          title={r.is_strategic ? "Quitar estratégico" : "Marcar estratégico"}
                          data-testid={`rot-strategic-toggle-${r.item_number}`}>
                          {r.is_strategic ? <Star size={13} fill="currentColor" /> : <StarOff size={13} />}
                        </button>
                      )}
                    </td>
                    <td className="font-bold">{r.item_number}</td>
                    <td>{r.description}</td>
                    <td>{r.warehouse}</td>
                    <td className="num font-bold">{fmtQty(r.qty_on_hand)}</td>
                    <td>{r.uom}</td>
                    <td className="num">{fmtMoney(r.avg_cost)}</td>
                    <td className="num font-bold">{fmtMoney(r.value)}</td>
                    <td className="num text-muted-foreground text-[11px]">{r.last_out_date ? fmtDate(r.last_out_date) : "—"}</td>
                    <td className="num tabular-nums">{r.days_since_last_out !== null ? r.days_since_last_out.toFixed(0) : "—"}</td>
                    <td>
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest border ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
